import { List, useTable } from '@refinedev/antd';
import { Table, Tag, Space, message, Switch, InputNumber, Button, Tooltip } from 'antd';
import { InfoCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { apiClient } from '../../lib/api-client';

interface Region {
  id: number;
  nameUz: string;
  nameRu: string;
}

interface SurgePricing {
  id: number;
  regionId: number;
  multiplier: number;
  isActive: boolean;
  requestsLastHour: number;
  driversOnline: number;
  region?: Region;
  updatedAt: string;
}

export const SurgePricingList = () => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingMultiplier, setEditingMultiplier] = useState<number>(1);

  const { tableProps, tableQueryResult } = useTable<SurgePricing>({
    resource: 'admin/pricing/surge',
    syncWithLocation: true,
  });

  const handleToggle = async (id: number) => {
    try {
      await apiClient.put(`/admin/pricing/surge/${id}/toggle`);
      message.success('Holat o\'zgartirildi');
      tableQueryResult.refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleUpdateMultiplier = async (id: number) => {
    try {
      await apiClient.put(`/admin/pricing/surge/${id}`, {
        multiplier: editingMultiplier,
      });
      message.success('Multiplikator yangilandi');
      setEditingId(null);
      tableQueryResult.refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleRecalculate = async () => {
    try {
      await apiClient.post('/admin/pricing/surge/recalculate');
      message.success('Surge narxlari qayta hisoblandi');
      tableQueryResult.refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const getMultiplierColor = (multiplier: number) => {
    if (multiplier >= 2) return 'red';
    if (multiplier >= 1.5) return 'orange';
    if (multiplier > 1) return 'gold';
    return 'default';
  };

  const getDemandColor = (requests: number, drivers: number) => {
    if (drivers === 0) return 'red';
    const ratio = requests / drivers;
    if (ratio >= 2) return 'red';
    if (ratio >= 1) return 'orange';
    return 'green';
  };

  const columns = [
    {
      title: 'Viloyat',
      key: 'region',
      render: (_: unknown, record: SurgePricing) =>
        record.region?.nameUz || `ID: ${record.regionId}`,
    },
    {
      title: (
        <Space>
          Multiplikator
          <Tooltip title="Narx ko'paytiruvchi. Masalan, 1.5 = 50% qo'shimcha">
            <InfoCircleOutlined style={{ color: '#7F8C8D' }} />
          </Tooltip>
        </Space>
      ),
      key: 'multiplier',
      render: (_: unknown, record: SurgePricing) => {
        if (editingId === record.id) {
          return (
            <Space>
              <InputNumber
                value={editingMultiplier}
                onChange={(val) => setEditingMultiplier(val || 1)}
                min={1}
                max={5}
                step={0.1}
                style={{ width: 80 }}
              />
              <Button
                type="primary"
                size="small"
                onClick={() => handleUpdateMultiplier(record.id)}
              >
                Saqlash
              </Button>
              <Button size="small" onClick={() => setEditingId(null)}>
                Bekor
              </Button>
            </Space>
          );
        }
        return (
          <Tag
            color={getMultiplierColor(record.multiplier)}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setEditingId(record.id);
              setEditingMultiplier(record.multiplier);
            }}
          >
            x{record.multiplier.toFixed(2)}
          </Tag>
        );
      },
    },
    {
      title: (
        <Space>
          So'rovlar/Haydovchilar
          <Tooltip title="Oxirgi 1 soatdagi so'rovlar va online haydovchilar soni">
            <InfoCircleOutlined style={{ color: '#7F8C8D' }} />
          </Tooltip>
        </Space>
      ),
      key: 'demand',
      render: (_: unknown, record: SurgePricing) => (
        <Tag color={getDemandColor(record.requestsLastHour, record.driversOnline)}>
          {record.requestsLastHour} / {record.driversOnline}
        </Tag>
      ),
    },
    {
      title: 'Holat',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (active: boolean, record: SurgePricing) => (
        <Switch
          checked={active}
          onChange={() => handleToggle(record.id)}
          checkedChildren="Faol"
          unCheckedChildren="Nofaol"
        />
      ),
    },
    {
      title: 'Yangilangan',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date: string) =>
        date ? new Date(date).toLocaleString('uz-UZ') : '-',
    },
  ];

  return (
    <List
      title="Surge narxlash"
      headerButtons={
        <Button icon={<SyncOutlined />} onClick={handleRecalculate}>
          Qayta hisoblash
        </Button>
      }
    >
      <div style={{ marginBottom: 16, padding: 16, background: '#F5F7FA', borderRadius: 8 }}>
        <p style={{ margin: 0, color: '#7F8C8D' }}>
          <InfoCircleOutlined style={{ marginRight: 8 }} />
          Surge narxlash avtomatik ravishda talab va taklifga qarab hisoblanadi.
          Qo'lda multiplikatorni o'zgartirish uchun qiymatni bosing.
        </p>
      </div>
      <Table {...tableProps} columns={columns} rowKey="id" />
    </List>
  );
};
